import { BundleOfSticksOwnershipResponse } from 'types/api-responses';
import {
  FormulaDefinition,
  FormulaType,
  InterestConversionData,
  InterestRowKey,
} from './types';
import { format8 } from 'utils/number';

/**
 * Builds formula data for the Interest Conversion Table from API response.
 *
 * Formula mappings based on the formula sheet:
 *
 * Interest Column (Acres to Decimal Conversion):
 * - Executive/Bonus/Delay (NMA): NMA / GrossAcres
 * - Executive/Bonus/Delay (NRA): ((NRA / (GrossAcres × 8)) + RoyaltyBurdens) / LeaseRoyaltyRate
 * - Royalty: Executive - RoyaltyBurdens
 * - WI: NMA / GrossAcres
 * - ORRI: NRA / (GrossAcres × 8)
 * - NRI (MI): Royalty × LeaseRoyaltyRate
 * - NRI (WI): WI × (1 - LeaseRoyaltyRate - ORRIBurdens)
 *
 * NMA Column (Output NMA):
 * - Executive (MI): Executive × GrossAcres
 * - WI: WI × GrossAcres
 *
 * NMA Post Burden Column:
 * - Royalty (MI): Royalty × GrossAcres
 * - WI: (WI_NRI × GrossAcres) / (1 - LeaseRoyaltyRate)
 * - ORRI: ORRI × GrossAcres
 *
 * NRA Column:
 * - NRI: GrossAcres × NRI × 8
 */

// Check if value is meaningful (not null and not zero for interests)
const hasMeaningfulValue = (
  value: number | null | undefined
): value is number => value !== null && value !== undefined;

// Create a formula definition with the new structure
const createFormula = (
  type: FormulaType,
  values: Record<string, number | null>,
  labels: Record<string, string>,
  resultValue: number | null,
  resultLabel: string
): FormulaDefinition => {
  // Build copy text based on formula type
  let copyText = '';
  switch (type) {
    case 'simple_division':
      copyText = `${format8(values.numerator)} / ${format8(
        values.denominator
      )} = ${format8(resultValue)}`;
      break;
    case 'division_with_multiplier':
      copyText = `${format8(values.numerator)} / (${format8(
        values.denominator1
      )} × ${format8(values.denominator2)}) = ${format8(resultValue)}`;
      break;
    case 'division_with_triple_multiplier':
      copyText = `${format8(values.numerator)} / (${format8(
        values.denominator1
      )} × ${format8(values.denominator2)} × ${format8(
        values.denominator3
      )}) = ${format8(resultValue)}`;
      break;
    case 'complex_nra_executive':
      copyText = `((${format8(values.nra)} / (${format8(
        values.grossAcres
      )} × 8)) + ${format8(values.royaltyBurdens)}) / ${format8(
        values.leaseRoyaltyRate
      )} = ${format8(resultValue)}`;
      break;
    case 'division_minus':
      copyText = `${format8(values.numerator)} / ${format8(
        values.denominator
      )} - ${format8(values.subtrahend)} = ${format8(resultValue)}`;
      break;
    case 'complex_nra_royalty':
      copyText = `((${format8(values.nra)} / (${format8(
        values.grossAcres
      )} × 8)) + ${format8(values.royaltyBurdens)}) / ${format8(
        values.leaseRoyaltyRate
      )} - ${format8(values.royaltyBurdens)} = ${format8(resultValue)}`;
      break;
    case 'simple_subtraction':
      copyText = `${format8(values.operand1)} - ${format8(
        values.operand2
      )} = ${format8(resultValue)}`;
      break;
    case 'simple_multiplication':
      copyText = `${format8(values.operand1)} × ${format8(
        values.operand2
      )} = ${format8(resultValue)}`;
      break;
    case 'wi_nri':
      copyText = `${format8(values.wi)} × (1 - ${format8(
        values.leaseRoyaltyRate
      )} - ${format8(values.orriBurdens)}) = ${format8(resultValue)}`;
      break;
    case 'wi_post_burden_nma':
      copyText = `(${format8(values.nri)} × ${format8(
        values.grossAcres
      )}) / (1 - ${format8(values.leaseRoyaltyRate)}) = ${format8(
        resultValue
      )}`;
      break;
    case 'triple_multiplication':
      copyText = `${format8(values.operand1)} × ${format8(
        values.operand2
      )} × ${format8(values.operand3)} = ${format8(resultValue)}`;
      break;
    case 'simple_passthrough':
      copyText = `${format8(values.operand1)} = ${format8(resultValue)}`;
      break;
  }

  return {
    type,
    values,
    labels,
    resultValue,
    resultLabel,
    copyText,
  };
};

// Build formula for mineral interest calculation (Executive/Bonus/Delay)
const buildMineralInterestFormula = (
  ownership: BundleOfSticksOwnershipResponse,
  interestValue: number | null
): FormulaDefinition | null => {
  const {
    grossAcres,
    nma,
    nra,
    leaseRoyaltyRate,
    royaltyBurdens,
    netAcreageUnits,
  } = ownership;

  if (!hasMeaningfulValue(grossAcres) || !hasMeaningfulValue(interestValue)) {
    return null;
  }

  if (netAcreageUnits === 'nma' && hasMeaningfulValue(nma)) {
    // NMA / GrossAcres = Interest
    return createFormula(
      'simple_division',
      { numerator: nma, denominator: grossAcres },
      { numerator: 'NMA', denominator: 'Gross Acres' },
      interestValue,
      'Interest'
    );
  }

  if (
    netAcreageUnits === 'nra' &&
    hasMeaningfulValue(nra) &&
    hasMeaningfulValue(leaseRoyaltyRate)
  ) {
    // ((NRA / (GrossAcres × 8)) + RoyaltyBurdens) / LeaseRoyaltyRate
    return createFormula(
      'complex_nra_executive',
      {
        nra,
        grossAcres,
        royaltyBurdens: royaltyBurdens ?? 0,
        leaseRoyaltyRate,
      },
      {
        nra: 'NRA',
        grossAcres: 'Gross Acres',
        royaltyBurdens: 'Royalty Burdens',
        leaseRoyaltyRate: 'Lease Royalty Rate',
      },
      interestValue,
      'Interest'
    );
  }

  return null;
};

// Build formula for Royalty interest
const buildRoyaltyInterestFormula = (
  ownership: BundleOfSticksOwnershipResponse
): FormulaDefinition | null => {
  const {
    royalty,
    executive,
    royaltyBurdens,
    grossAcres,
    netAcreageUnits,
    leaseRoyaltyRate,
  } = ownership;

  if (!hasMeaningfulValue(royalty)) {
    return null;
  }

  // MD/UMI (has executive): Royalty = Executive - RoyaltyBurdens
  if (hasMeaningfulValue(executive)) {
    return createFormula(
      'simple_subtraction',
      { operand1: executive, operand2: royaltyBurdens ?? 0 },
      { operand1: 'Executive', operand2: 'Royalty Burdens' },
      royalty,
      'Royalty'
    );
  }

  // NPRI with NMA (no executive): Royalty = NMA / GrossAcres - RoyaltyBurdens
  // Derive NMA from: NMA = (Royalty + RoyaltyBurdens) × GrossAcres
  if (netAcreageUnits === 'nma' && hasMeaningfulValue(grossAcres)) {
    const burdens = royaltyBurdens ?? 0;
    const derivedNma = (royalty + burdens) * grossAcres;
    return createFormula(
      'division_minus',
      { numerator: derivedNma, denominator: grossAcres, subtrahend: burdens },
      {
        numerator: 'NMA',
        denominator: 'Gross Acres',
        subtrahend: 'Royalty Burdens',
      },
      royalty,
      'Royalty'
    );
  }

  // NPRI with NRA (no executive): Royalty = ((NRA / (GrossAcres × 8)) + RoyaltyBurdens) / LeaseRoyaltyRate - RoyaltyBurdens
  // Derive NRA from the inverse calculation
  if (
    netAcreageUnits === 'nra' &&
    hasMeaningfulValue(grossAcres) &&
    hasMeaningfulValue(leaseRoyaltyRate)
  ) {
    const burdens = royaltyBurdens ?? 0;
    // Inverse: NRA = ((Royalty + RoyaltyBurdens) × LeaseRoyaltyRate - RoyaltyBurdens) × GrossAcres × 8
    const derivedNra =
      ((royalty + burdens) * leaseRoyaltyRate - burdens) * grossAcres * 8;
    return createFormula(
      'complex_nra_royalty',
      {
        nra: derivedNra,
        grossAcres,
        royaltyBurdens: burdens,
        leaseRoyaltyRate,
      },
      {
        nra: 'NRA',
        grossAcres: 'Gross Acres',
        royaltyBurdens: 'Royalty Burdens',
        leaseRoyaltyRate: 'Lease Royalty Rate',
      },
      royalty,
      'Royalty'
    );
  }

  return null;
};

// Build formula for WI interest
const buildWIInterestFormula = (
  ownership: BundleOfSticksOwnershipResponse
): FormulaDefinition | null => {
  const { wi, nma, grossAcres, netAcreageUnits } = ownership;

  if (!hasMeaningfulValue(wi) || netAcreageUnits !== 'nma') {
    return null;
  }

  if (!hasMeaningfulValue(nma) || !hasMeaningfulValue(grossAcres)) {
    return null;
  }

  // WI = NMA / GrossAcres
  return createFormula(
    'simple_division',
    { numerator: nma, denominator: grossAcres },
    { numerator: 'NMA', denominator: 'Gross Acres' },
    wi,
    'WI'
  );
};

// Build formula for ORRI interest
const buildORRIInterestFormula = (
  ownership: BundleOfSticksOwnershipResponse
): FormulaDefinition | null => {
  const { orri, nra, grossAcres, netAcreageUnits } = ownership;

  if (!hasMeaningfulValue(orri) || !hasMeaningfulValue(grossAcres)) {
    return null;
  }

  if (netAcreageUnits === 'nra' && hasMeaningfulValue(nra)) {
    // ORRI = NRA / (GrossAcres × 8)
    return createFormula(
      'division_with_multiplier',
      { numerator: nra, denominator1: grossAcres, denominator2: 8 },
      { numerator: 'NRA', denominator1: 'Gross Acres', denominator2: '8' },
      orri,
      'ORRI'
    );
  }

  return null;
};

// Build formula for NRI interest
const buildNRIInterestFormula = (
  ownership: BundleOfSticksOwnershipResponse
): FormulaDefinition | null => {
  const { nri, royalty, wi, orri, leaseRoyaltyRate, orriBurdens } = ownership;

  if (!hasMeaningfulValue(nri)) {
    return null;
  }

  // MI NRI: Royalty × LeaseRoyaltyRate (for MD, NPRI)
  if (hasMeaningfulValue(royalty) && hasMeaningfulValue(leaseRoyaltyRate)) {
    return createFormula(
      'simple_multiplication',
      { operand1: royalty, operand2: leaseRoyaltyRate },
      { operand1: 'Royalty', operand2: 'Lease Royalty Rate' },
      nri,
      'NRI'
    );
  }

  // WI NRI: WI × (1 - LeaseRoyaltyRate - ORRIBurdens) (for OGL)
  if (hasMeaningfulValue(wi) && hasMeaningfulValue(leaseRoyaltyRate)) {
    return createFormula(
      'wi_nri',
      {
        wi,
        leaseRoyaltyRate,
        orriBurdens: orriBurdens ?? 0,
      },
      {
        wi: 'WI',
        leaseRoyaltyRate: 'Lease Royalty Rate',
        orriBurdens: 'ORRI Burdens',
      },
      nri,
      'NRI'
    );
  }

  // ORRI NRI: NRI = ORRI (passthrough for ORRI instrument type)
  if (hasMeaningfulValue(orri)) {
    return createFormula(
      'simple_passthrough',
      { operand1: orri },
      { operand1: 'ORRI' },
      nri,
      'NRI'
    );
  }

  return null;
};

// Build formula for Output NMA
const buildOutputNMAFormula = (
  ownership: BundleOfSticksOwnershipResponse
): FormulaDefinition | null => {
  const { nma, executive, wi, grossAcres } = ownership;

  if (!hasMeaningfulValue(nma) || !hasMeaningfulValue(grossAcres)) {
    return null;
  }

  // MI: Executive × GrossAcres = NMA
  if (hasMeaningfulValue(executive)) {
    return createFormula(
      'simple_multiplication',
      { operand1: executive, operand2: grossAcres },
      { operand1: 'Executive', operand2: 'Gross Acres' },
      nma,
      'NMA'
    );
  }

  // WI: WI × GrossAcres = NMA
  if (hasMeaningfulValue(wi)) {
    return createFormula(
      'simple_multiplication',
      { operand1: wi, operand2: grossAcres },
      { operand1: 'WI', operand2: 'Gross Acres' },
      nma,
      'NMA'
    );
  }

  return null;
};

// Build formula for Post Burden NMA
const buildPostBurdenNMAFormula = (
  ownership: BundleOfSticksOwnershipResponse
): FormulaDefinition | null => {
  const {
    postBurdenNma,
    royalty,
    wi,
    nri,
    orri,
    grossAcres,
    leaseRoyaltyRate,
  } = ownership;

  if (!hasMeaningfulValue(postBurdenNma) || !hasMeaningfulValue(grossAcres)) {
    return null;
  }

  // MI: Royalty × GrossAcres = Post Burden NMA
  if (hasMeaningfulValue(royalty)) {
    return createFormula(
      'simple_multiplication',
      { operand1: royalty, operand2: grossAcres },
      { operand1: 'Royalty', operand2: 'Gross Acres' },
      postBurdenNma,
      'Post Burden NMA'
    );
  }

  // WI: (NRI × GrossAcres) / (1 - LeaseRoyaltyRate) = Post Burden NMA
  if (
    hasMeaningfulValue(wi) &&
    hasMeaningfulValue(nri) &&
    hasMeaningfulValue(leaseRoyaltyRate)
  ) {
    return createFormula(
      'wi_post_burden_nma',
      { nri, grossAcres, leaseRoyaltyRate },
      {
        nri: 'NRI',
        grossAcres: 'Gross Acres',
        leaseRoyaltyRate: 'Lease Royalty Rate',
      },
      postBurdenNma,
      'Post Burden NMA'
    );
  }

  // ORRI: ORRI × GrossAcres = Post Burden NMA
  if (hasMeaningfulValue(orri)) {
    return createFormula(
      'simple_multiplication',
      { operand1: orri, operand2: grossAcres },
      { operand1: 'ORRI', operand2: 'Gross Acres' },
      postBurdenNma,
      'Post Burden NMA'
    );
  }

  return null;
};

// Build formula for Output NRA
const buildOutputNRAFormula = (
  ownership: BundleOfSticksOwnershipResponse
): FormulaDefinition | null => {
  const { nra, nri, grossAcres } = ownership;

  if (
    !hasMeaningfulValue(nra) ||
    !hasMeaningfulValue(nri) ||
    !hasMeaningfulValue(grossAcres)
  ) {
    return null;
  }

  // NRA = GrossAcres × NRI × 8
  return createFormula(
    'triple_multiplication',
    { operand1: grossAcres, operand2: nri, operand3: 8 },
    { operand1: 'Gross Acres', operand2: 'NRI', operand3: '8' },
    nra,
    'NRA'
  );
};

/**
 * Converts API response to InterestConversionData with formulas populated.
 * @param {BundleOfSticksOwnershipResponse} ownership - The ownership response from the API
 * @return {InterestConversionData} The converted interest conversion data
 */
export const buildInterestConversionData = (
  ownership: BundleOfSticksOwnershipResponse
): InterestConversionData => {
  const {
    executive,
    bonus,
    delay,
    royalty,
    wi,
    orri,
    nri,
    nma,
    postBurdenNma,
    nra,
    leaseRoyaltyRate,
  } = ownership;

  // Determine which row should show NMA (Executive for MD/UMI, WI for OGL)
  const showExecutiveNMA = hasMeaningfulValue(executive);
  const showWINMA = !showExecutiveNMA && hasMeaningfulValue(wi);

  // Determine which row should show Post Burden NMA
  // Based on instrument type matrix:
  // - MD, NPRI, UMI: Royalty row
  // - OGL: NRI row (when WI is present)
  // - ORRI: ORRI row
  const showRoyaltyPostBurden = hasMeaningfulValue(royalty);
  const showNRIPostBurden = !showRoyaltyPostBurden && hasMeaningfulValue(wi); // OGL case
  const showORRIPostBurden =
    !showRoyaltyPostBurden && !showNRIPostBurden && hasMeaningfulValue(orri);

  return {
    executive: {
      interest: {
        value: executive,
        showCalculation: hasMeaningfulValue(executive),
        formula: buildMineralInterestFormula(ownership, executive),
      },
      nma: {
        value: showExecutiveNMA ? nma : null,
        showCalculation: showExecutiveNMA && hasMeaningfulValue(nma),
        formula: showExecutiveNMA ? buildOutputNMAFormula(ownership) : null,
      },
      nmaPostBurden: { value: null, showCalculation: false },
      nra: { value: null, showCalculation: false },
    },
    royalty: {
      interest: {
        value: royalty,
        showCalculation: hasMeaningfulValue(royalty),
        formula: buildRoyaltyInterestFormula(ownership),
      },
      nma: { value: null, showCalculation: false },
      nmaPostBurden: {
        value: showRoyaltyPostBurden ? postBurdenNma : null,
        showCalculation:
          showRoyaltyPostBurden && hasMeaningfulValue(postBurdenNma),
        formula: showRoyaltyPostBurden
          ? buildPostBurdenNMAFormula(ownership)
          : null,
      },
      nra: { value: null, showCalculation: false },
    },
    bonus: {
      interest: {
        value: bonus,
        showCalculation: hasMeaningfulValue(bonus),
        formula: buildMineralInterestFormula(ownership, bonus),
      },
      nma: { value: null, showCalculation: false },
      nmaPostBurden: { value: null, showCalculation: false },
      nra: { value: null, showCalculation: false },
    },
    delay: {
      interest: {
        value: delay,
        showCalculation: hasMeaningfulValue(delay),
        formula: buildMineralInterestFormula(ownership, delay),
      },
      nma: { value: null, showCalculation: false },
      nmaPostBurden: { value: null, showCalculation: false },
      nra: { value: null, showCalculation: false },
    },
    leaseRoyaltyRate: {
      interest: {
        value: leaseRoyaltyRate,
        showCalculation: false, // User input, no formula
      },
      nma: { value: null, showCalculation: false },
      nmaPostBurden: { value: null, showCalculation: false },
      nra: { value: null, showCalculation: false },
    },
    orri: {
      interest: {
        value: orri,
        showCalculation: hasMeaningfulValue(orri),
        formula: buildORRIInterestFormula(ownership),
      },
      nma: { value: null, showCalculation: false },
      nmaPostBurden: {
        value: showORRIPostBurden ? postBurdenNma : null,
        showCalculation:
          showORRIPostBurden && hasMeaningfulValue(postBurdenNma),
        formula: showORRIPostBurden
          ? buildPostBurdenNMAFormula(ownership)
          : null,
      },
      nra: { value: null, showCalculation: false },
    },
    wi: {
      interest: {
        value: wi,
        showCalculation: hasMeaningfulValue(wi),
        formula: buildWIInterestFormula(ownership),
      },
      nma: {
        value: showWINMA ? nma : null,
        showCalculation: showWINMA && hasMeaningfulValue(nma),
        formula: showWINMA ? buildOutputNMAFormula(ownership) : null,
      },
      nmaPostBurden: { value: null, showCalculation: false }, // OGL Post Burden goes to NRI row
      nra: { value: null, showCalculation: false },
    },
    nri: {
      interest: {
        value: nri,
        showCalculation: hasMeaningfulValue(nri),
        formula: buildNRIInterestFormula(ownership),
      },
      nma: { value: null, showCalculation: false },
      nmaPostBurden: {
        value: showNRIPostBurden ? postBurdenNma : null,
        showCalculation: showNRIPostBurden && hasMeaningfulValue(postBurdenNma),
        formula: showNRIPostBurden
          ? buildPostBurdenNMAFormula(ownership)
          : null,
      },
      nra: {
        value: nra,
        showCalculation: hasMeaningfulValue(nra),
        formula: buildOutputNRAFormula(ownership),
      },
    },
  };
};

/**
 * Creates empty InterestConversionData for when no data is available.
 * @return {InterestConversionData} Empty interest conversion data structure
 */
export const createEmptyInterestConversionData = (): InterestConversionData => {
  const emptyRow = () => ({
    interest: { value: null, showCalculation: false },
    nma: { value: null, showCalculation: false },
    nmaPostBurden: { value: null, showCalculation: false },
    nra: { value: null, showCalculation: false },
  });

  const rowKeys: InterestRowKey[] = [
    'executive',
    'royalty',
    'bonus',
    'delay',
    'leaseRoyaltyRate',
    'orri',
    'wi',
    'nri',
  ];

  return rowKeys.reduce(
    (acc, key) => ({ ...acc, [key]: emptyRow() }),
    {} as InterestConversionData
  );
};
